import type { PracticeLocation } from '../types';
import { US_STATE_CODES } from './usStates';

const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
/** City: letters, spaces, periods, apostrophes, hyphens; allows unicode letters */
const CITY_REGEX = /^[\p{L}\s'.-]+$/u;
/** Street: typical US address characters */
const STREET_REGEX = /^[\w\s#.,'&/()-]+$/u;

export function emptyPracticeLocation(): PracticeLocation {
  return {
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  };
}

/** Normalize ZIP to NNNNN or NNNNN-NNNN */
export function normalizeUsZip(zip: string): string {
  const z = zip.trim();
  const digits = z.replace(/\D/g, '');
  if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  if (digits.length === 5) return digits;
  return z;
}

function coerceLocationObject(raw: Record<string, unknown>): PracticeLocation {
  // Support both camelCase (frontend) and snake_case (backend/MongoDB) field names
  const a2 = raw.address2 ?? raw.address_2 ?? raw.line2 ?? raw.street2;
  return {
    address1: String(
      raw.address1 ?? raw.address_1 ?? raw.street ?? raw.street_address ?? raw.line1 ?? ''
    ).trim(),
    address2: a2 != null && String(a2).trim() ? String(a2).trim() : '',
    city: String(raw.city ?? '').trim(),
    state: String(raw.state ?? raw.state_code ?? '').trim().toUpperCase().slice(0, 2),
    zipCode: normalizeUsZip(
      String(raw.zipCode ?? raw.zip_code ?? raw.zip ?? raw.postal_code ?? raw.postalCode ?? '')
    ),
  };
}

/**
 * Accepts legacy `string[]` or structured objects from API.
 */
export function normalizePracticeLocations(raw: unknown): PracticeLocation[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item): PracticeLocation => {
    if (typeof item === 'string') {
      const t = item.trim();
      if (!t) return emptyPracticeLocation();
      return { ...emptyPracticeLocation(), address1: t };
    }
    if (item && typeof item === 'object') {
      return coerceLocationObject(item as Record<string, unknown>);
    }
    return emptyPracticeLocation();
  });
}

export type LocationFieldErrors = Partial<Record<keyof PracticeLocation, string>>;

export function validateUSPracticeLocation(loc: PracticeLocation): { valid: boolean; errors: LocationFieldErrors } {
  const errors: LocationFieldErrors = {};

  const a1 = loc.address1.trim();
  if (a1.length < 2) {
    errors.address1 = 'Address line 1 is required (at least 2 characters).';
  } else if (a1.length > 120) {
    errors.address1 = 'Address line 1 must be at most 120 characters.';
  } else if (!STREET_REGEX.test(a1)) {
    errors.address1 = 'Address line 1 contains invalid characters for a US address.';
  }

  const a2 = (loc.address2 || '').trim();
  if (a2.length > 120) {
    errors.address2 = 'Address line 2 must be at most 120 characters.';
  } else if (a2 && !STREET_REGEX.test(a2)) {
    errors.address2 = 'Address line 2 contains invalid characters.';
  }

  const city = loc.city.trim();
  if (!city) {
    errors.city = 'City is required.';
  } else if (city.length > 80) {
    errors.city = 'City must be at most 80 characters.';
  } else if (!CITY_REGEX.test(city)) {
    errors.city = 'City may only include letters, spaces, and . \' -';
  }

  const st = loc.state.trim().toUpperCase();
  if (!st) {
    errors.state = 'State is required.';
  } else if (!/^[A-Z]{2}$/.test(st) || !US_STATE_CODES.has(st)) {
    errors.state = 'Please choose a valid US state or DC.';
  }

  const zip = loc.zipCode.trim();
  if (!zip) {
    errors.zipCode = 'ZIP code is required.';
  } else if (!ZIP_REGEX.test(normalizeUsZip(zip))) {
    errors.zipCode = 'Use a valid US ZIP (12345 or 12345-6789).';
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/** True if every field is empty (row can be discarded). */
export function isPracticeLocationEmpty(loc: PracticeLocation): boolean {
  return (
    !loc.address1.trim() &&
    !(loc.address2 || '').trim() &&
    !loc.city.trim() &&
    !loc.state.trim() &&
    !loc.zipCode.trim()
  );
}

export function sanitizePracticeLocation(loc: PracticeLocation): PracticeLocation {
  return {
    address1: loc.address1.trim(),
    address2: (loc.address2 || '').trim() || '',
    city: loc.city.trim(),
    state: loc.state.trim().toUpperCase().slice(0, 2),
    zipCode: normalizeUsZip(loc.zipCode),
  };
}

export function formatPracticeLocationOneLine(loc: PracticeLocation): string {
  const s = sanitizePracticeLocation(loc);
  const line1 = [s.address1, s.address2].filter(Boolean).join(', ');
  const tail = [s.city, s.state, s.zipCode].filter(Boolean).join(', ');
  return [line1, tail].filter(Boolean).join(' · ');
}
